import { keepPreviousData } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { userService } from '@/services/userService';
import { roleService } from '@/services/roleService';
import { APIError } from '@/utils/api-client';
import type { User, UserFormData, UserInviteData } from '@/types/user';

// User hooks
export const useUsers = () => {
  return useApiQuery(queryKeys.users.lists(), () => userService.getUsers(), {
    select: (data) => data.users,
  });
};

// `search` is matched server-side, so pass it debounced - each distinct value
// is its own query key and its own request. Previous matches stay on screen
// while the next ones load, so refining a search does not blank the picker.
export const useActiveUsers = (search?: string) => {
  return useApiQuery(
    queryKeys.users.active(search),
    () => userService.getActiveUsers(search),
    { placeholderData: keepPreviousData }
  );
};

export const useUpdateUser = () => {
  return useApiMutation<
    User,
    APIError,
    { userId: string; data: Partial<UserFormData> }
  >(({ userId, data }) => userService.updateUser(userId, data), {
    invalidateKeys: (_data, variables) => [
      queryKeys.users.all,
      queryKeys.users.detail(variables.userId),
    ],
    successMessage: 'User updated successfully',
    mutationKey: ['users', 'update'],
  });
};

export const useInviteUser = () => {
  return useApiMutation<
    Awaited<ReturnType<typeof userService.inviteUser>>,
    APIError,
    UserInviteData
  >((data) => userService.inviteUser(data), {
    invalidateKeys: [queryKeys.users.all],
    successMessage: 'User invited successfully',
    mutationKey: ['users', 'invite'],
  });
};

export const useDeactivateUser = () => {
  return useApiMutation<{ success: boolean }, APIError, string>(
    (userId) => userService.deactivateUser(userId),
    {
      invalidateKeys: [queryKeys.users.all],
      successMessage: 'User deactivated successfully',
      mutationKey: ['users', 'deactivate'],
    }
  );
};

export const useReactivateUser = () => {
  return useApiMutation<{ success: boolean }, APIError, string>(
    (userId) => userService.activateUser(userId),
    {
      invalidateKeys: [queryKeys.users.all],
      successMessage: 'User reactivated successfully',
      mutationKey: ['users', 'reactivate'],
    }
  );
};

export const useResendInvitation = () => {
  return useApiMutation<{ success: boolean }, APIError, string>(
    (userId) => userService.resendInvitation(userId),
    {
      invalidateKeys: [queryKeys.users.all],
      successMessage: 'Invitation resent successfully',
      mutationKey: ['users', 'resendInvitation'],
    }
  );
};

export const useAssignUserRoles = () => {
  return useApiMutation<
    { success: boolean },
    APIError,
    { userId: string; roleIds: string[] }
  >(({ userId, roleIds }) => userService.assignUserRoles(userId, roleIds), {
    invalidateKeys: (_data, variables) => [
      queryKeys.users.all,
      queryKeys.users.detail(variables.userId),
    ],
    successMessage: 'User roles updated successfully',
    mutationKey: ['users', 'assignRoles'],
  });
};

// Role hooks
export const useRoles = () => {
  return useApiQuery(queryKeys.roles.lists(), () => roleService.getRoles(), {
    select: (data) => data.roles,
  });
};
